import { computed, Ref } from 'vue';
import { store } from '@/store';
import CartLineItem from '@/models/entities/cart/cart-line-item';
import OrderType from '@/models/entities/cart/order-type';
import ItemSummary from '@/models/entities/items/item-summary';
import { EntryMethod } from '@/models/entities/cart/entry-method';

/** A line item in the cart-sync payload (see `cartDtoFromCart` in the cart store). */
interface CartSyncItem {
  id: string;
  item: string;
  variation: string | null;
  quantity: number;
  weightedItemQuantity: number | null;
  embeddedPrice?: number;
  originalPrice?: number;
  embeddedDiscount?: number;
  expiration?: string;
  substitutable?: boolean;
  instructions?: string;
  entryMethod?: EntryMethod;
}

interface GiftCardItem {
  id: string;
  amount: number;
  quantity: number;
  design: string;
  recipientName?: string;
  senderName?: string;
  recipientEmail?: string;
  recipientPhoneNumber?: string;
  print?: boolean;
  sendDate?: string;
}

interface SpecialOrderItem {
  id: string;
  description: string;
  quantity: number;
}

interface CartSyncDto {
  items?: CartSyncItem[];
  lineItems?: CartLineItem[];
  giftCardItems?: GiftCardItem[];
  specialOrderItems?: SpecialOrderItem[];
}

interface MultiCart {
  orderType: string;
  lineItems?: CartLineItem[];
  giftCardItems?: GiftCardItem[];
  specialOrderItems?: SpecialOrderItem[];
}

/**
 * `orderType` was a mixin prop — pass `toRef(props, 'orderType')` from the consuming component,
 * or omit it entirely when the component does not work with order types.
 */
export default function useCartItemControls(orderType?: Ref<OrderType | null | undefined>) {
  const cart = computed<CartSyncDto | null>(() => store.getters['cart/getCartSyncDto']);
  const getLineItems = computed<CartLineItem[]>(() => store.getters['cart/getLineItems']);
  const getGiftCardItems = computed<GiftCardItem[]>(() => store.getters['cart/getGiftCardItems']);
  const isCustomerModeScan = computed<boolean>(() => store.getters['user/isCustomerModeScan']);
  const nonEmptyCarts = computed<MultiCart[] | null>(() => store.getters['cart/nonEmptyCarts']);

  const cartLineItems = computed<CartLineItem[]>(() => {
    if (orderType?.value && nonEmptyCarts.value && !isCustomerModeScan.value) {
      const orderTypeCart = nonEmptyCarts.value.find(c => c.orderType === orderType.value!.id);
      return orderTypeCart?.lineItems ?? [];
    }

    return getLineItems.value;
  });

  const getLineItemsOfType = (type: number): CartLineItem[] => {
    return cartLineItems.value.filter(lineItem => lineItem.type === type);
  };

  /**
   * The line item for the requested inventory item
   */
  const getItemLineItem = (
    item: ItemSummary | null,
    variation: string | null = null,
  ): CartLineItem | null | undefined => {
    if (!item) {
      return null;
    }

    return getLineItemsOfType(1).find(lineItem => {
      return (
        lineItem.item.id === item.id &&
        ((!lineItem.variation && !variation) || lineItem.variation === variation)
      );
    });
  };

  const lineItemCount = (multiCart?: MultiCart | null): number => {
    let lineItems: CartLineItem[];

    if (multiCart?.lineItems) {
      lineItems = multiCart.lineItems.filter(
        lineItem => lineItem.type === 1 && lineItem.quantity >= 0,
      );
    } else {
      lineItems = getLineItemsOfType(1).filter(lineItem => lineItem.quantity >= 0);
    }

    return lineItems.length;
  };

  const specialOrderItemLineItemCount = (multiCart?: MultiCart | null): number => {
    let lineItems: CartLineItem[];
    if (multiCart?.lineItems) {
      lineItems = multiCart.lineItems.filter(
        lineItem => lineItem.type === 60 && lineItem.quantity >= 0,
      );
    } else {
      lineItems = getLineItemsOfType(60).filter(lineItem => lineItem.quantity >= 0);
    }

    return lineItems.length;
  };

  const isEmpty = computed<boolean>(() => {
    return (
      (!cart.value?.items?.length || lineItemCount() === 0) && specialOrderItemLineItemCount() === 0
    );
  });

  const cartLineItemCount = (orderTypeCart?: MultiCart | null): number => {
    if (orderTypeCart) {
      return orderTypeCart.giftCardItems ||
        orderTypeCart.specialOrderItems ||
        orderTypeCart.lineItems
        ? lineItemCount(orderTypeCart) +
            (orderTypeCart.giftCardItems as GiftCardItem[]).length +
            specialOrderItemLineItemCount(orderTypeCart)
        : 0;
    }
    return cart.value &&
      (cart.value.giftCardItems || cart.value.specialOrderItems || cart.value.lineItems)
      ? lineItemCount() + getGiftCardItems.value.length + specialOrderItemLineItemCount()
      : 0;
  };

  return {
    cart,
    getLineItems,
    getGiftCardItems,
    isCustomerModeScan,
    cartLineItems,
    isEmpty,
    getItemLineItem,
    getLineItemsOfType,
    lineItemCount,
    specialOrderItemLineItemCount,
    cartLineItemCount,
  };
}
